import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { useEffect, useMemo, useState } from "react";
import {
  buildAwsCron,
  parseAwsCronToBuilder,
  ScheduleBuilderState,
  summarizeAwsCron,
  timesShareMinute,
  WEEKDAYS,
} from "utils/awsCron";
import NumberField from "./NumberField";

/**
 * The schedule builder form, shared by ScheduleBuilderDialog (Settings,
 * immediate create/update) and the registration wizard (draft mode). State
 * lives in `useScheduleForm`; the caller decides what to do with
 * `form.cron` / `form.name`.
 */

const DEFAULT_BUILDER: ScheduleBuilderState = {
  frequency: "daily",
  times: ["09:00"],
  weekdays: ["MON"],
  dayOfMonth: 1,
};

const DEFAULT_RAW_CRON = "cron(0 9 * * ? *)";

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "HH:MM" → Date (fixed date, time only) for the TimeField; null if invalid. */
function timeStringToDate(value: string): Date | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(2000, 0, 1, Number(m[1]), Number(m[2]));
}

function dateToTimeString(d: Date | null): string {
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Time picker with an interactive analog clock that COMMITS only on OK:
 * while the clock is open, selections update a local draft; Cancel/dismiss
 * reverts to the committed value. Typing with the picker closed commits
 * directly (it is deliberate keyboard input, not clock browsing).
 */
function ScheduleTimePicker({
  value,
  onCommit,
}: {
  value: Date | null;
  onCommit: (d: Date | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Date | null>(value);

  // Resync the draft when the committed value changes from outside.
  const committedMs = value?.getTime() ?? null;
  useEffect(() => {
    setDraft(value);
    // eslint-disable-next-line
  }, [committedMs]);

  return (
    <TimePicker
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setDraft(value); // Cancel/dismiss reverts; OK re-syncs via commit
      }}
      value={draft}
      onChange={(d) => {
        setDraft(d);
        if (!open) onCommit(d);
      }}
      onAccept={(d) => onCommit(d)}
      closeOnSelect={false}
      ampm
      ampmInClock
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
      }}
      slotProps={{
        textField: {
          size: "small",
          sx: { width: 180 },
          inputProps: { "aria-label": "Run time" },
        },
        actionBar: { actions: ["cancel", "accept"] },
        popper: {
          sx: (theme) => ({
            "& .MuiPaper-root": {
              boxShadow: theme.shadows[12],
              border: "1px solid",
              borderColor: "divider",
            },
          }),
        },
      }}
    />
  );
}

/** Client-side preview of the EventBridge slug the backend will generate. */
export function previewScheduleSlug(displayName: string): string {
  return (
    displayName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "schedule"
  );
}

export interface ScheduleFormInitial {
  displayName: string;
  cron: string;
}

export interface UseScheduleFormResult {
  name: string;
  setName: (v: string) => void;
  advanced: boolean;
  setAdvanced: (v: boolean) => void;
  rawCron: string;
  setRawCron: (v: string) => void;
  builder: ScheduleBuilderState;
  setBuilder: React.Dispatch<React.SetStateAction<ScheduleBuilderState>>;
  cron: string;
  summary: string;
  minuteOk: boolean;
  weekdayOk: boolean;
  isValid: boolean;
  reset: (initial?: ScheduleFormInitial) => void;
}

export function useScheduleForm(): UseScheduleFormResult {
  const [name, setName] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [rawCron, setRawCron] = useState(DEFAULT_RAW_CRON);
  const [builder, setBuilder] = useState<ScheduleBuilderState>(DEFAULT_BUILDER);

  const cron = advanced ? rawCron.trim() : buildAwsCron(builder);
  const summary = useMemo(() => summarizeAwsCron(cron), [cron]);

  const minuteOk = advanced || timesShareMinute(builder.times);
  const weekdayOk =
    advanced || builder.frequency !== "weekly" || builder.weekdays.length > 0;
  const isValid = !!name.trim() && !!cron && minuteOk && weekdayOk;

  const reset = (initial?: ScheduleFormInitial) => {
    if (initial) {
      setName(initial.displayName);
      const parsed = parseAwsCronToBuilder(initial.cron);
      if (parsed) {
        setBuilder(parsed);
        setAdvanced(false);
      } else {
        setRawCron(initial.cron);
        setAdvanced(true);
      }
    } else {
      setName("");
      setBuilder(DEFAULT_BUILDER);
      setRawCron(DEFAULT_RAW_CRON);
      setAdvanced(false);
    }
  };

  return {
    name,
    setName,
    advanced,
    setAdvanced,
    rawCron,
    setRawCron,
    builder,
    setBuilder,
    cron,
    summary,
    minuteOk,
    weekdayOk,
    isValid,
    reset,
  };
}

export default function ScheduleFormFields({
  form,
  eventBridgeName,
}: {
  form: UseScheduleFormResult;
  // Edit mode passes the immutable stored slug; otherwise a live preview.
  eventBridgeName?: string;
}) {
  const {
    name,
    setName,
    advanced,
    setAdvanced,
    rawCron,
    setRawCron,
    builder,
    setBuilder,
    cron,
    summary,
    minuteOk,
  } = form;

  const setTime = (i: number, value: string) =>
    setBuilder((b) => ({
      ...b,
      times: b.times.map((t, idx) => (idx === i ? value : t)),
    }));
  const addTime = () =>
    setBuilder((b) => ({ ...b, times: [...b.times, "12:00"] }));
  const removeTime = (i: number) =>
    setBuilder((b) => ({ ...b, times: b.times.filter((_, idx) => idx !== i) }));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          size="small"
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText={`EventBridge name: ${
            eventBridgeName ?? previewScheduleSlug(name)
          }`}
        />

        <FormControlLabel
          control={
            <Switch checked={advanced} onChange={(_, c) => setAdvanced(c)} />
          }
          label="Advanced (raw cron)"
        />

        {advanced ? (
          <TextField
            size="small"
            label="EventBridge cron"
            value={rawCron}
            onChange={(e) => setRawCron(e.target.value)}
            placeholder="cron(0 9 * * ? *)"
            sx={{ fontFamily: "monospace" }}
          />
        ) : (
          <>
            <TextField
              select
              size="small"
              label="Frequency"
              value={builder.frequency}
              onChange={(e) =>
                setBuilder((b) => ({
                  ...b,
                  frequency: e.target
                    .value as ScheduleBuilderState["frequency"],
                }))
              }
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>

            {builder.frequency === "weekly" && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  On days
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={builder.weekdays}
                  onChange={(_, days: string[]) =>
                    setBuilder((b) => ({ ...b, weekdays: days }))
                  }
                >
                  {WEEKDAYS.map((d) => (
                    <ToggleButton key={d} value={d}>
                      {d}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            {builder.frequency === "monthly" && (
              <NumberField
                label="Day of month"
                value={builder.dayOfMonth}
                min={1}
                max={31}
                onChange={(v) => setBuilder((b) => ({ ...b, dayOfMonth: v }))}
                sx={{ width: 160 }}
              />
            )}

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                At time(s)
              </Typography>
              <Stack spacing={1}>
                {builder.times.map((t, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <ScheduleTimePicker
                      value={timeStringToDate(t)}
                      onCommit={(d) => setTime(i, dateToTimeString(d))}
                    />
                    {builder.times.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeTime(i)}
                        aria-label="Remove time"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addTime}
                sx={{ mt: 1 }}
              >
                Add time
              </Button>
            </Box>

            {!minuteOk && (
              <Alert severity="warning">
                All times must share the same minute (EventBridge cron allows
                one minute with multiple hours). Use a separate schedule
                otherwise.
              </Alert>
            )}
          </>
        )}

        <Box>
          <Typography variant="body2" color="text.secondary">
            Summary
          </Typography>
          <Typography variant="body1">{summary}</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {cron}
          </Typography>
        </Box>
      </Stack>
    </LocalizationProvider>
  );
}
